import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Mails, Paperclip, CheckCircle2 } from "lucide-react";
import { BulkEmailStep } from "@/components/marketing/BulkEmailStep";
import { downloadPersonalizedAttachment } from "@/lib/attachments";

export const BulkEmailDialog = ({ prospects, onClose }) => {
  const open = !!prospects?.length;
  const [setup, setSetup] = useState(null); // {me, templates, mine}
  const [tid, setTid] = useState("");
  const [myName, setMyName] = useState("");
  const [started, setStarted] = useState(false);
  const [i, setI] = useState(0);
  const [sent, setSent] = useState({});
  const [busy, setBusy] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!open) return;
    setSetup(null); setStarted(false); setI(0); setSent({}); setTid(""); setLoadError("");
    Promise.all([api.get("/marketing/personalize/defaults"), api.get("/marketing/assets"), api.get("/marketing/templates/mine").catch(() => ({ data: {} }))])
      .then(([{ data: me }, { data: a }, { data: mine }]) => {
        const templates = (a.sections || []).flatMap((s) => s.assets || []).filter((x) => x.email_body && (!x.channel || x.channel === "email"));
        setSetup({ me, templates, mine: mine || {} });
        setTid(templates[0]?.id || "");
        setMyName(me.contact_name && me.contact_name !== me.shop_name ? me.contact_name : "");
      }).catch((e) => setLoadError(apiError(e.response?.data?.detail) || "Couldn't load the email templates."));
  }, [open, reload]); // eslint-disable-line react-hooks/exhaustive-deps

  const asset = setup?.templates.find((t) => t.id === tid);
  const vals = useMemo(() => ({ ...(setup?.me || {}), my_name: myName.trim(), bike_path: (setup?.me.bike_paths || [])[0] || "" }), [setup, myName]);
  const tpl = useMemo(() => setup?.mine[tid] || { subject: asset?.email_subject, body: asset?.email_body }, [setup, tid, asset]);
  const total = prospects?.length || 0;
  const finished = open && started && i >= total;
  const sentCount = Object.keys(sent).length;

  const send = async (to) => {
    const p = prospects[i];
    setBusy(true);
    try {
      if (to && to !== (p.email || "")) await api.patch("/prospects/batch", { updates: [{ id: p.id, email: to }] });
      if (!sent[p.id]) await api.post(`/prospects/${p.id}/outreach`, { channel: "email", template: asset?.title });
      setSent((s) => ({ ...s, [p.id]: true }));
      setTimeout(() => setI((x) => x + 1), 400);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't mark the prospect as emailed"); }
    finally { setBusy(false); }
  };
  const attach = async () => {
    setAttaching(true);
    try { await downloadPersonalizedAttachment(asset.attach_asset_id, setup.me, myName); toast.success("Flyer downloaded. Attach it to each email."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't download the flyer"); }
    finally { setAttaching(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(sentCount); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="bulk-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mails className="w-5 h-5 text-primary" />Email {total} prospect{total === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>Each email is filled in with your details and the business's details. It opens in your own email app so you can send it, and the prospect is marked as Emailed with a follow-up date.</DialogDescription>
        </DialogHeader>
        {!open ? null : loadError ? (
          <div className="py-6 text-center space-y-3" data-testid="bulk-email-load-error">
            <p className="text-sm text-destructive">{loadError}</p>
            <Button variant="outline" onClick={() => setReload((r) => r + 1)} data-testid="bulk-email-retry">Try again</Button>
          </div>
        ) : !setup ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          : !setup.templates.length ? <p className="py-6 text-sm text-center text-muted-foreground" data-testid="bulk-email-no-templates">No email templates are available yet. Ask Velopaths to add them under Marketing Assets.</p>
          : finished ? (
            <div className="py-6 text-center space-y-2" data-testid="bulk-email-finished">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
              <div className="font-semibold">All done</div>
              <p className="text-sm text-muted-foreground" data-testid="bulk-email-summary">{sentCount} marked as emailed, {total - sentCount} skipped.</p>
            </div>
          ) : started ? (
            <BulkEmailStep p={prospects[i]} index={i} total={total} tpl={tpl} vals={vals} done={!!sent[prospects[i].id]} busy={busy}
              onSend={send} onSkip={() => setI((x) => x + 1)} onBack={() => setI((x) => Math.max(0, x - 1))} />
          ) : (
            <div className="space-y-4" data-testid="bulk-email-setup">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Email template</Label>
                  <Select value={tid} onValueChange={setTid}>
                    <SelectTrigger data-testid="bulk-email-template-select"><SelectValue placeholder="Choose a template" /></SelectTrigger>
                    <SelectContent>{setup.templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}{setup.mine[t.id] ? " (your version)" : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Your name</Label><Input value={myName} onChange={(e) => setMyName(e.target.value)} placeholder="e.g. Jamie Rivera" data-testid="bulk-email-my-name" /></div>
              </div>
              <p className="text-sm text-muted-foreground" data-testid="bulk-email-missing-count">{prospects.filter((p) => !p.email).length} of {total} have no email address on file. You can type one in as you go, or skip them.</p>
              {asset?.attach_asset_id && (
                <div className="rounded-xl border border-border bg-secondary/40 p-3 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="bulk-email-attachment">
                  <div className="text-sm"><b className="flex items-center gap-1.5"><Paperclip className="w-4 h-4 text-primary" />Attach the {asset.attach_title || "flyer"}</b><span className="text-xs text-muted-foreground">Download it once, then attach it to each email.</span></div>
                  <Button variant="outline" onClick={attach} disabled={attaching} data-testid="bulk-email-download-attachment">{attaching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Paperclip className="w-4 h-4 mr-2" />}Download flyer</Button>
                </div>
              )}
            </div>
          )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose(sentCount)} data-testid="bulk-email-close">{finished ? "Done" : "Close"}</Button>
          {open && setup && !started && <Button onClick={() => setStarted(true)} disabled={!asset} data-testid="bulk-email-start">Start</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
